"use client";

import { useMemo } from "react";
import { useAuth } from "react-oidc-context";

import { isTestEnv } from "@/lib/env";
import {
  type InactivityConfig,
  readInactivityConfig,
  resolveInactivityEnabled,
} from "@/lib/resolveInactivityEnabled";

export interface UseInactivityEnabledResult {
  /** Whether inactivity logout is on for this session (the idle timer runs). */
  enabled: boolean;
  /** The env-derived config read at mount, including the timer timing. */
  config: InactivityConfig;
}

/**
 * The single source for "is inactivity logout enabled". `useIdleLogout` drives
 * the idle timer from it and `InactivityCrashLogout` gates its fail-closed
 * logout on it, so the crash fallback logs out exactly when the timer it
 * replaces would have been running.
 *
 * The env-derived config is read once per mount and fixed for its life.
 */
export function useInactivityEnabled(): UseInactivityEnabledResult {
  const auth = useAuth();

  const config = useMemo(() => readInactivityConfig(), []);
  const { disabledFlag, e2eOverride } = config;

  // In a controlled E2E run the mock auth provider authenticates the session at
  // the route gate, but react-oidc-context's `useAuth()` does NOT reflect that
  // (the mock forces auth as an `AuthController` prop, not inside the OIDC
  // context), so `auth.isAuthenticated` is false. Treat the test-only E2E
  // override as satisfying the auth requirement so the feature can be exercised
  // under Playwright's fake clock. `e2eOverride` is test-env-only (the
  // build-time `NEXT_PUBLIC_INACTIVITY_LOGOUT_E2E` flag or the runtime window
  // flag, the latter gated on `isTestEnv`), so production auth gating is
  // unchanged — in prod `e2eOverride` is always false.
  const isAuthenticated = (auth.isAuthenticated ?? false) || e2eOverride;

  const enabled = resolveInactivityEnabled({
    disabledFlag,
    isTestEnv,
    e2eOverride,
    isAuthenticated,
  });

  return useMemo(() => ({ enabled, config }), [enabled, config]);
}
